/**
 * `FrozenMap<K, V>` — universal read-only Map wrapper.
 *
 * **Why this exists:** `Object.freeze(new Map())` is illusory. Maps store
 * their entries in an internal `[[MapData]]` slot that `Object.freeze`
 * does not touch — `map.set(k, v)` keeps working after the freeze, and
 * the new entry sticks. `FrozenMap` solves this by hiding the underlying
 * `Map` behind an ECMAScript private field (`#data`), so consumers
 * literally cannot reach the mutator methods.
 *
 * Ported near-verbatim from `ersatzteil-scraper` `src/lib/utils/frozen-map.ts`
 * (relative imports only — ADR-0005). It backs the versioned-plugin factory's
 * immutable `schemas` / `migrations` accessors.
 *
 * **Read-only API surface:** `get`, `has`, `keys`, `values`, `entries`,
 * `forEach`, `size`, `[Symbol.iterator]()`, `toArray()`. No `set`, `delete`,
 * or `clear`. Structurally compatible with `ReadonlyMap<K, V>`.
 *
 * @see src-js/lib/storage/README.md (versioned-envelope code pattern)
 */
export class FrozenMap<K, V> {
  /**
   * Underlying mutable Map — kept private via an ECMAScript class private
   * field so consumers cannot reach `.set` / `.delete` / `.clear`.
   */
  readonly #data: Map<K, V>;

  /**
   * Builds a `FrozenMap` from any iterable of `[key, value]` pairs. The
   * entries are copied into a fresh internal Map; the source iterable is not
   * retained (insertion order preserved).
   */
  constructor(entries: Iterable<readonly [K, V]>) {
    this.#data = new Map(entries);
  }

  /** O(1) amortized — delegates to the native Map. */
  get(key: K): V | undefined {
    return this.#data.get(key);
  }

  /** O(1) amortized — delegates to the native Map. */
  has(key: K): boolean {
    return this.#data.has(key);
  }

  /** Iterator over keys in insertion order. */
  keys(): IterableIterator<K> {
    return this.#data.keys();
  }

  /** Iterator over values in insertion order. */
  values(): IterableIterator<V> {
    return this.#data.values();
  }

  /** Iterator over `[key, value]` pairs in insertion order. */
  entries(): IterableIterator<[K, V]> {
    return this.#data.entries();
  }

  /**
   * `Map.forEach`-shaped iteration. The callback signature mirrors the native
   * `(value, key)` order. The native 3-arg form `(value, key, map)` is NOT
   * forwarded — leaking the inner Map would defeat the `#data` encapsulation.
   */
  forEach(cb: (value: V, key: K) => void): void {
    this.#data.forEach((value, key) => {
      cb(value, key);
    });
  }

  /** Number of entries currently held. */
  get size(): number {
    return this.#data.size;
  }

  /** Iterable protocol — enables `for (const [k, v] of frozenMap)`. */
  [Symbol.iterator](): IterableIterator<[K, V]> {
    return this.#data[Symbol.iterator]();
  }

  /** A fresh `V[]` snapshot (O(n) copy). */
  toArray(): V[] {
    return Array.from(this.#data.values());
  }
}
